import { GlobalStateController } from "bai-react-global-state";
import type { User } from "firebase/auth";
import _ from "lodash";
import React from "react";
import type Stripe from "stripe";
import { StripeElements } from "@stripe/stripe-js";

import { presentations as presentationsApi } from "apis/callables";
import { workspacesBilling as workspacesBillingApi } from "apis-v2/callables";
import { ErrorResponseBody } from "apis/_sdk/types/errors";
import { PusherEventType } from "common/constants";
import {
    IUserStorageModel,
    IWorkspaceDocumentChangedEvent,
    MetricName,
    WorkspacePlanTier
} from "common/interfaces";
import stripeProducts, { RecurringPlanDefinition } from "common/stripeProducts";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import WorkspaceController from "js/controllers/WorkspaceController";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import pusher, { ExtendedChannel } from "js/core/services/pusher";
import { getStripe, handleStripeErrors } from "js/core/services/stripe";
import * as StripeUtils from "common/utils/stripeUtils";
import { trackActivity } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import Spinner from "js/react/components/Spinner";
import { ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import Analytics from "js/analytics";

const logger = getLogger(LogGroup.BILLING);

export type GetWorkspaceStripeDataResponse = Awaited<ReturnType<typeof workspacesBillingApi.getWorkspaceStripeData>>;
type UpgradeWorkspacePlanRequest = Parameters<typeof workspacesBillingApi.upgradeWorkspacePlan>[0];

export class StripeRadarError extends Error {
    public readonly responseBody: ErrorResponseBody;
    public readonly cause: unknown;

    constructor(originalError: any) {
        super(originalError.message);
        this.cause = originalError;
        this.name = "StripeRadarError";
    }
}

export interface BillingControllerState {
    initialized: boolean;
    initializeError: Error | null;
    workspaceId: string;
    stripeData: GetWorkspaceStripeDataResponse;
}

const initialState: BillingControllerState = {
    initialized: false,
    initializeError: null,
    workspaceId: null,
    stripeData: {
        customer: null,
        subscription: null,
        upcomingInvoice: null,
        invoices: {},
        paymentMethods: {}
    }
};

class BillingController extends GlobalStateController<BillingControllerState> {
    private _firebaseUser: User;
    private _pusherChannel: ExtendedChannel;
    private _pusherChannelUnbind: () => void
    private _runSequentially: SequentialRunner
    private _userModel: IUserStorageModel;

    public get initialized() {
        return this._state.initialized;
    }

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    public async initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        await this._runSequentially(async () => {
            logger.info("[BillingController] initialize()", { workspaceId, uid: firebaseUser.uid });

            await this._reset(false);

            try {
                this._firebaseUser = firebaseUser;
                this._userModel = userModel;
                const stripeData = await workspacesBillingApi.getWorkspaceStripeData({ workspaceId });

                this._pusherChannel = await pusher.subscribe(`private-workspace-${workspaceId}`);
                this._pusherChannelUnbind = this._pusherChannel.bindChunked(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

                await this._updateState({
                    initialized: true,
                    stripeData,
                    workspaceId
                });
            } catch (err) {
                logger.error(err, "[BillingController] initialize() failed");

                await this._updateState({
                    ..._.cloneDeep(initialState),
                    initialized: false,
                    initializeError: err
                });
            }
        });
    }

    get subscription() {
        return this._state.stripeData.subscription;
    }

    get isInTrial() {
        return this._state.stripeData.subscription?.status === "trialing";
    }

    get invoices() {
        return this._state.stripeData.invoices;
    }

    get usedSeatCount() {
        return WorkspaceController.usedSeatCount;
    }

    get paidSeatCount() {
        if (!this._state.stripeData.subscription) {
            return 0;
        }

        return this._state.stripeData.subscription.items.data[0].quantity;
    }

    get availableSeatCount() {
        return Math.max(this.paidSeatCount - this.usedSeatCount, 0);
    }

    get hasPaymentMethod() {
        return Object.keys(this._state.stripeData.paymentMethods).length > 0;
    }

    get canChangeSubscriptionQuantity() {
        return this.hasPaymentMethod && !WorkspaceController.plan.isManaged;
    }

    get workspaceId() {
        return this._state.workspaceId;
    }

    get paymentMethod() {
        const { subscription, paymentMethods } = this._state.stripeData;

        return StripeUtils.PaymentMethod.getDefaultPaymentMethod(subscription, Object.values(paymentMethods));
    }

    get stripeData() {
        return this._state.stripeData;
    }

    protected _stateDidUpdate(prevState: BillingControllerState) {
        const { stripeData, initialized } = this._state;

        if (!initialized) {
            return;
        }

        if (prevState.initialized && stripeData?.subscription?.status && prevState.stripeData?.subscription?.status !== stripeData?.subscription?.status) {
            this._userModel.loadWorkspacesMetadata([this._state.workspaceId])
                .catch(err => {
                    logger.error(err, "[BillingController] _stateDidUpdate() failed to reload user workspaces metadata");
                });
        }
    }

    public async reset() {
        return this._reset();
    }

    public async reloadStripeData() {
        return this._runSequentially(async () => {
            const stripeData = await workspacesBillingApi.getWorkspaceStripeData({ workspaceId: this._state.workspaceId });
            await this._updateState({ stripeData });
        });
    }

    public async cancelSubscription() {
        return this._runSequentially(async () => {
            const subscription = await workspacesBillingApi.cancelSubscription({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription } }));
        });
    }

    public async downgradeSubscription(targetTier: WorkspacePlanTier) {
        return this._runSequentially(async () => {
            const { subscription, upcomingInvoice } = await workspacesBillingApi.downgradeSubscription({ workspaceId: this._state.workspaceId, targetTier });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, upcomingInvoice } }));
        });
    }

    public async revertPendingDowngrade() {
        return this._runSequentially(async () => {
            const { subscription, upcomingInvoice } = await workspacesBillingApi.revertPendingDowngrade({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, upcomingInvoice } }));
        });
    }

    public async reactivateSubscription() {
        return this._runSequentially(async () => {
            const subscription = await workspacesBillingApi.reactivateSubscription({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription } }));
        });
    }

    public async switchSubscriptionBillingInterval(billingInterval: "month" | "year") {
        return this._runSequentially(async () => {
            const { subscription, upcomingInvoice } = await workspacesBillingApi.switchSubscriptionBillingInterval({ workspaceId: this._state.workspaceId, billingInterval });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, upcomingInvoice } }));
        });
    }

    public async updatePaymentMethod(paymentMethodId: string) {
        return this._runSequentially(async () => {
            const prevPaymentMethod = this.paymentMethod;
            const { subscription, paymentMethods } = await workspacesBillingApi.updatePaymentMethod({ workspaceId: this._state.workspaceId, paymentMethodId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, paymentMethods } }));

            const eventProps = {
                method_changed_from: StripeUtils.PaymentMethod.getPaymentMethodName(prevPaymentMethod),
                method_changed_to: StripeUtils.PaymentMethod.getPaymentMethodName(this.paymentMethod)
            };
            trackActivity("PaymentMethod", "Update", null, null, eventProps, { audit: true });
        });
    }

    public async payLatestInvoiceOnPastDueSubscription() {
        return this._runSequentially(async () => {
            const response = await workspacesBillingApi.payLatestInvoiceOnPastDueSubscription({ workspaceId: this._state.workspaceId });

            if (response.actionRequired) {
                try {
                    const stripe = await getStripe();
                    await handleStripeErrors(() => stripe.confirmPayment({
                        clientSecret: response.paymentIntent.client_secret,
                        confirmParams: {
                            return_url: window.location.href,
                            payment_method: response.paymentIntent.payment_method as string
                        },
                        redirect: "if_required"
                    }));
                    logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                        success: true,
                        customerId: response.paymentIntent.customer,
                    });
                } catch (err) {
                    logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                        success: false,
                        customerId: response.paymentIntent.customer,
                        errorMessage: err.message
                    });
                    throw err;
                }
            }

            const stripeData = await workspacesBillingApi.getWorkspaceStripeData({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData }));
        });
    }

    public async endTrialOnSubscription() {
        return this._runSequentially(async () => {
            const { subscription, upcomingInvoice, invoices } = await workspacesBillingApi.endTrialOnSubscription({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, upcomingInvoice, invoices } }));
        });
    }

    public async applyPromotionCodeToSubscription(promotionCode: string) {
        return this._runSequentially(async () => {
            const { subscription, upcomingInvoice } = await workspacesBillingApi.applyPromotionCodeToSubscription({ workspaceId: this._state.workspaceId, promotionCode });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, upcomingInvoice } }));
        });
    }

    public async updateBillingDetails(update: { address?: Stripe.Address, name?: string, email?: string, taxId?: Pick<Stripe.TaxId, "type" | "value"> }) {
        return this._runSequentially(async () => {
            const customer = await workspacesBillingApi.updateBillingDetails({ workspaceId: this._state.workspaceId, ...update });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, customer } }));
        });
    }

    public async updateSubscriptionQuantity(quantity: number) {
        return this._runSequentially(async () => {
            const currentQuantity = this.paidSeatCount;

            const response = await workspacesBillingApi.updateSubscriptionQuantity({ workspaceId: this._state.workspaceId, quantity });

            if (response.actionRequired === false) {
                await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription: response.subscription } }));
            }

            if (response.actionRequired === true) {
                const { paymentIntent, invoiceId } = response;
                try {
                    const stripe = await getStripe();
                    await handleStripeErrors(() => stripe.confirmPayment({
                        clientSecret: paymentIntent.client_secret,
                        confirmParams: {
                            return_url: window.location.href,
                            payment_method: paymentIntent.payment_method as string
                        },
                        redirect: "if_required"
                    }));
                    logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                        success: true,
                        customerId: paymentIntent.customer
                    });
                } catch (err) {
                    logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                        success: false,
                        customerId: paymentIntent.customer,
                        errorMessage: err.message
                    });
                    throw err;
                }
                const subscription = await workspacesBillingApi.finalizeUpdateSubscriptionQuantity({ workspaceId: this._state.workspaceId, quantity, invoiceId });
                await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription } }));
            }

            (quantity > currentQuantity)
                ? Analytics.ModifyWorkspaceUsers.QuantityExpansion({ quantity_new_seats_added: quantity - currentQuantity })
                : Analytics.ModifyWorkspaceUsers.QuantityContraction({ quantity_seats_removed: currentQuantity - quantity });

            //// Legacy analytics ////
            if (quantity > currentQuantity) {
                const paymentMethod = this.paymentMethod;
                const billingProps = {
                    workspace_id: this._state.workspaceId,
                    seats_added: quantity - currentQuantity,
                    type: "expansion",
                    total_seats: quantity,
                    payment_method: StripeUtils.PaymentMethod.getPaymentMethodName(paymentMethod)
                };

                trackActivity("Organization", "BillingIntentComplete", null, null, billingProps, { audit: true });
                await Api.klaviyoTrack.post({
                    eventName: "Organization:BillingIntentComplete",
                    billingProps,
                });
            }
            ////
        });
    }

    /**
     * Wraps the given component with a state injector that renders the component only if the controller
     * is initialized.
     *
     * !! THIS WRAPPER WILL *NOT* INITIALIZE THE CONTROLLER IF IT'S NOT ALREADY INITIALIZED !!
     */
    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { initialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!initialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    private _onPusherEvent = (documentChangeEvents: IWorkspaceDocumentChangedEvent[]) => {
        this._runSequentially(async () => {
            for (const event of documentChangeEvents) {
                if (event.documentType === "WorkspacePlan") {
                    try {
                        const stripeData = await workspacesBillingApi.getWorkspaceStripeData({ workspaceId: this._state.workspaceId });
                        this._updateState({ stripeData });
                    } catch (err) {
                        logger.error(err, "[BillingController] _onPusherEvent() failed");
                    }
                }
            }
        });
    }

    private _reset(runSequentially = true) {
        const reset = async () => {
            logger.info("[BillingController] reset()");

            this._firebaseUser = null;
            this._userModel = null;

            if (this._pusherChannel) {
                this._pusherChannelUnbind();
                if (!this._pusherChannel.isInUse) {
                    pusher.unsubscribe(this._pusherChannel.name);
                }
                this._pusherChannel = null;
                this._pusherChannelUnbind = null;
            }

            await this._updateState(_.cloneDeep(initialState));
        };

        if (runSequentially) {
            return this._runSequentially(reset);
        }

        return reset();
    }
}

const billingController = new BillingController();

// for lazy import and debug
app.billingController = billingController;

export default billingController;

/**
 * Upgrades a workspace plan
 *
 * Stateless, but will force refresh on the controller if initialized
 */
export async function upgradeWorkspacePlan(options: UpgradeWorkspacePlanRequest & { analyticsProps?: { [key: string]: any; } }) {
    const { workspaceId } = options;

    let { state, subscription } = await workspacesBillingApi.upgradeWorkspacePlan(options);

    if (state.type === "incomplete") {
        try {
            if (state.actionType === "confirm_payment") {
                try {
                    const stripe = await getStripe();
                    await handleStripeErrors(() => stripe.confirmPayment({
                        clientSecret: state.paymentIntent.client_secret,
                        confirmParams: {
                            return_url: window.location.href,
                            payment_method: state.paymentIntent.payment_method as string
                        },
                        redirect: "if_required"
                    }));
                    logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                        success: true,
                        customerId: subscription.customer
                    });
                } catch (err) {
                    logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                        success: false,
                        customerId: subscription.customer,
                        errorMessage: err.message
                    });
                    throw err;
                }
            }

            // Finalize is only called if the subscription require additional actions
            ({ state, subscription } = await workspacesBillingApi.finalizeUpgradeWorkspacePlan({ workspaceId }));
        } catch (err) {
            logger.error(err, "[BillingController] upgradeWorkspacePlan() failed to confirm payment/setup, will abort upgrade");

            // Abort will remove the subscription so we don't have to wait for it to go into incomplete_expired state itself
            // Commenting this out to prevent reoccurrence of BA-19368 while we investigate a proper fix
            // await workspacesBillingApi.abortUpgradeWorkspacePlan({ workspaceId });

            throw err;
        }
    }

    if (state.type === "active" && state.actionType === "confirm_setup") {
        try {
            const stripe = await getStripe();
            await handleStripeErrors(() => stripe.confirmSetup({
                clientSecret: state.setupIntent.client_secret,
                confirmParams: {
                    return_url: window.location.href,
                    payment_method: state.setupIntent.payment_method as string
                },
                redirect: "if_required"
            }));
            logger.metric(MetricName.SETUP_CONFIRMATION_ATTEMPT, {
                success: true,
                customerId: subscription.customer
            });
        } catch (err) {
            logger.metric(MetricName.SETUP_CONFIRMATION_ATTEMPT, {
                success: false,
                customerId: subscription.customer,
                errorMessage: err.message
            });
            logger.error(err, "[BillingController] upgradeWorkspacePlan() failed to confirm setup, will notify user");

            await ShowMessageDialog({
                title: "Warning",
                message: <>
                    There was an issue authorizing your payment method for future payments.
                    <br />
                    Your subscription was activated, but please update your payment method to make sure future payments are successful and your subscription is not interrupted.
                </>,
                buttonOptions: { color: "blue" }
            });
        }
    }

    await handleSuccessfulWorkspacePlanUpgrade({
        workspaceId,
        promotionCode: "promotionCode" in options ? options.promotionCode : undefined,
        priceId: "priceId" in options ? options.priceId : undefined,
        quantity: "quantity" in options ? options.quantity : undefined,
        tier: options.tier,
        subscriptionId: subscription.id,
        analyticsProps: options.analyticsProps
    });
}

/**
 * Analytics reporting and internal state updates that are done after a successful workspace plan upgrade
 *
 * Stateless
 */
export async function handleSuccessfulWorkspacePlanUpgrade(options: {
    workspaceId: string,
    promotionCode?: string,
    priceId?: string,
    quantity?: number,
    tier: WorkspacePlanTier,
    subscriptionId?: string,
    analyticsProps?: { [key: string]: any; }
}) {
    const { workspaceId, promotionCode, priceId, quantity, tier, subscriptionId, analyticsProps } = options;

    // Legacy
    if (tier === WorkspacePlanTier.PRO) {
        try {
            const productDefinition = stripeProducts[tier];
            const priceDefinition = productDefinition.plans.find(plan => "planId" in plan && plan.planId === priceId) as RecurringPlanDefinition;

            const { slideCount } = await presentationsApi.getSlideCount({ workspaceId, filter: "all" });
            const stripeData = await workspacesBillingApi.getWorkspaceStripeData({ workspaceId });
            const paymentMethod = stripeData.subscription?.default_payment_method || Object.values(stripeData.paymentMethods).sort((a, b) => b.created - a.created)[0];
            const eventProps = {
                billing_term: priceDefinition.interval,
                current_status: WorkspaceController.plan.type,
                amount: priceDefinition.amount / 100,
                plan: "pro",
                current_slide_count: slideCount,
                subscription_id: subscriptionId,
                workspace_id: workspaceId,
                promo_code: promotionCode,
                price_id: priceId,
                plan_display_name: productDefinition.productName,
                user_id: auth().currentUser.uid,
                payment_method: StripeUtils.PaymentMethod.getPaymentMethodName(paymentMethod),
                ...(analyticsProps ?? {})
            };
            trackActivity("Upgrade", "BillingIntentComplete", null, null, eventProps, { audit: true });
            await Api.klaviyoTrack.post({
                eventName: "Upgrade:BillingIntentComplete",
                eventProps,
            });

            trackActivity("SignupFlow", "NavForward", null, null, {
                step_number: 8,
                step_name: "Checkout",
                plan_selected: "pro",
                object: "button",
                object_label: "Start free trial",
                action: "clicked",
                workspace_id: "all"
            }, { audit: true });
        } catch (err) {
            logger.error(err, "[BillingController] handleSuccessfulWorkspacePlanUpgrade() legacy analytics failed");
        }
    } else if (tier === WorkspacePlanTier.TEAM) {
        const productDefinition = stripeProducts[tier];
        const priceDefinition = productDefinition.plans.find(plan => "planId" in plan && plan.planId === priceId) as RecurringPlanDefinition;
        const stripeData = await workspacesBillingApi.getWorkspaceStripeData({ workspaceId });
        const paymentMethod = stripeData.subscription?.default_payment_method || Object.values(stripeData.paymentMethods).sort((a, b) => b.created - a.created)[0];

        const billingProps = {
            workspace_id: workspaceId,
            seats_added: quantity,
            type: "first_purchase",
            billing_term: priceDefinition.interval,
            total_seats: quantity,
            promo_code: promotionCode,
            price_id: priceId,
            plan_display_name: productDefinition.productName,
            payment_method: StripeUtils.PaymentMethod.getPaymentMethodName(paymentMethod),
            ...(analyticsProps ?? {})
        };
        trackActivity("Organization", "BillingIntentComplete", null, null, billingProps, { audit: true });
        await Api.klaviyoTrack.post({
            eventName: "Organization:BillingIntentComplete",
            billingProps,
        });
    }
    // End Legacy

    if (billingController.initialized && billingController.workspaceId === workspaceId) {
        await billingController.reloadStripeData();
    }
    if (WorkspaceController.initialized && WorkspaceController.workspaceId === workspaceId) {
        await WorkspaceController.forceRefreshWorkspace();
    }
    if (app.user) {
        await app.user.loadWorkspacesMetadata([workspaceId]);
        await app.user.loadAvailableTrialDays();
    }
}

/**
 * Checks a payment method against Stripe Radar without authorizing or creating a charge
 *
 * Stateless
 */
export async function stripeRadarPaymentCheck({ workspaceId, paymentMethod, elements }: {
    paymentMethod: Stripe.PaymentMethod,
    elements: StripeElements,
    workspaceId: string,
}) {
    try {
        const stripe = await getStripe();

        const setupIntent = await workspacesBillingApi.createSetupIntent({ workspaceId });

        const confirmSetupIntentResult = await stripe.confirmSetup({
            elements,
            clientSecret: setupIntent.client_secret,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: "if_required"
        });

        if (confirmSetupIntentResult.error) {
            throw new StripeRadarError(confirmSetupIntentResult.error);
        }

        if (confirmSetupIntentResult.setupIntent.status !== "succeeded") {
            throw new StripeRadarError(`[BillingController] stripe.confirmSetup returned with status ${confirmSetupIntentResult.setupIntent?.status}, please investigate`);
        }

        Analytics.PaymentMethod.VerificationSuccess({ paymentMethod, workspaceId });
    } catch (error) {
        Analytics.PaymentMethod.VerificationFail({ workspaceId, error });
        throw error;
    }
}

/**
 * Ensures the workspace has a linked customer and payment method
 *
 * Stateless
 */
export async function ensureCustomerAndPaymentMethod(request: Parameters<typeof workspacesBillingApi.ensureCustomerAndPaymentMethod>[0]) {
    return await workspacesBillingApi.ensureCustomerAndPaymentMethod(request);
}

/**
 * Returns the stripe data for a workspace
 *
 * Stateless if the controller is not initialized
 */
export async function getWorkspaceStripeData(workspaceId: string) {
    if (billingController.initialized && billingController.workspaceId === workspaceId) {
        return billingController.stripeData;
    }

    return await workspacesBillingApi.getWorkspaceStripeData({ workspaceId });
}

/**
 * Calculates the tax amount for a workspace
 *
 * Stateless
 */
export async function calculateTaxAmount(request: Parameters<typeof workspacesBillingApi.calculateTaxAmount>[0]) {
    return await workspacesBillingApi.calculateTaxAmount(request);
}

/**
 * Returns the promotion code for a workspace
 *
 * Stateless
 */
export async function getPromotionCode(promotionCode: string) {
    return await workspacesBillingApi.getPromotionCode({ promotionCode });
}

/**
 * Creates a checkout session for a workspace
 *
 * Stateless
 */
export async function createCheckoutSession(request: Parameters<typeof workspacesBillingApi.createCheckoutSession>[0]) {
    return await workspacesBillingApi.createCheckoutSession(request);
}

/**
 * Updates a checkout session for a workspace
 *
 * Stateless
 */
export async function updateCheckoutSession(request: Parameters<typeof workspacesBillingApi.updateCheckoutSession>[0]) {
    return await workspacesBillingApi.updateCheckoutSession(request);
}
